// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Card, Col, Row, Spin} from "antd";
import {CheckCircleFilled, CloseCircleFilled} from "@ant-design/icons";
import * as AuthBackend from "./AuthBackend";
import * as Setting from "../Setting";
import i18next from "i18next";

class ConsentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      info: null,
      pendingId: null,
      submitting: false,
      approved: false,
      denied: false,
    };
  }

  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search);
    const pendingId = params.get("pendingId");
    this.setState({pendingId});
    AuthBackend.getConsentInfo(pendingId).then(res => {
      if (res.status === "ok") {
        this.setState({info: res.data, loading: false});
      } else {
        Setting.showMessage("error", res.msg);
        this.setState({loading: false});
      }
    });
  }

  approve() {
    this.setState({submitting: true});
    AuthBackend.approveConsent(this.state.pendingId).then(res => {
      if (res.status === "ok") {
        const code = res.data;
        const state = res.data2;
        const redirectUri = this.state.info.redirectUri;
        const sep = redirectUri.includes("?") ? "&" : "?";
        const callbackUrl = `${redirectUri}${sep}code=${code}&state=${state}`;
        const isCustomScheme = !redirectUri.startsWith("http://") && !redirectUri.startsWith("https://");
        if (isCustomScheme) {
          this.setState({submitting: false, approved: true});
          window.location.href = callbackUrl;
        } else {
          fetch(callbackUrl, {method: "GET"}).catch(() => {});
          this.setState({submitting: false, approved: true});
        }
      } else {
        Setting.showMessage("error", res.msg);
        this.setState({submitting: false});
      }
    });
  }

  deny() {
    AuthBackend.denyConsent(this.state.pendingId).then(() => {
      const redirectUri = this.state.info.redirectUri;
      const state = this.state.info.state;
      const sep = redirectUri.includes("?") ? "&" : "?";
      const denyUrl = `${redirectUri}${sep}error=access_denied&state=${state}`;
      const isCustomScheme = !redirectUri.startsWith("http://") && !redirectUri.startsWith("https://");
      if (isCustomScheme) {
        this.setState({denied: true});
      } else {
        fetch(denyUrl, {method: "GET"}).catch(() => {});
        this.setState({denied: true});
      }
    });
  }

  renderDenied() {
    return (
      <Row justify="center" align="middle" style={{minHeight: "60vh", width: "100%"}}>
        <Col style={{textAlign: "center"}}>
          <CloseCircleFilled style={{fontSize: 64, color: "#ff4d4f"}} />
          <p style={{fontSize: 20, marginTop: 24}}>
            {i18next.t("general:Authorization denied")}
          </p>
        </Col>
      </Row>
    );
  }

  renderSuccess() {
    const appName = this.state.info?.displayName || this.state.info?.appName || "";
    return (
      <Row justify="center" align="middle" style={{minHeight: "60vh", width: "100%"}}>
        <Col style={{textAlign: "center"}}>
          <CheckCircleFilled style={{fontSize: 64, color: "#52c41a"}} />
          <p style={{fontSize: 20, marginTop: 24, marginBottom: 8}}>
            {i18next.t("application:Login successful")}
          </p>
          <p style={{color: "#888", fontSize: 14}}>
            {i18next.t("application:Please return to")} <b>{appName}</b> {i18next.t("application:to continue using")}
          </p>
        </Col>
      </Row>
    );
  }

  render() {
    if (this.state.loading) {
      return <Spin size="large" style={{display: "block", marginTop: "200px"}} />;
    }
    if (!this.state.info) {
      return null;
    }
    if (this.state.approved) {
      return this.renderSuccess();
    }
    if (this.state.denied) {
      return this.renderDenied();
    }

    const {info} = this.state;
    const account = this.props.account;
    const accountName = account?.displayName || account?.name || "";
    return (
      <div style={{width: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 80}}>
        <Card
          title={`${i18next.t("general:Authorize")} ${info.displayName}`}
          style={{width: 480, maxWidth: "90vw", textAlign: "center"}}
        >
          <p>
            <b>{info.displayName}</b>
            {i18next.t("general:requests access to your account")}
          </p>
          {accountName && (
            <p style={{color: "#555", fontSize: 13, marginTop: -8}}>
              {i18next.t("general:Logged in as")}：<b>{accountName}</b>
            </p>
          )}
          <p style={{color: "#888", fontSize: 12, wordBreak: "break-all"}}>
            {i18next.t("general:Redirect to")}：{info.redirectUri}
          </p>
          <div style={{display: "flex", justifyContent: "center", gap: 16, marginTop: 24}}>
            <Button
              type="primary"
              loading={this.state.submitting}
              onClick={() => this.approve()}
            >
              {i18next.t("general:Approve")}
            </Button>
            <Button onClick={() => this.deny()}>
              {i18next.t("general:Deny")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }
}

export default ConsentPage;
